import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Button,
  Input,
  Select,
  SimpleGrid,
  Text,
  useToast,
} from "@chakra-ui/react";
import { register, getRoles } from "services/authService";

const isSalesRole = (roleName = "") =>
  roleName.toUpperCase().includes("SALES");

const EMPTY_FORM = {
  userName: "",
  email: "",
  password: "",
  roleId: "",
  auditorCode: "",
};

function FieldFrame({ label, required, error, gridColumn, children }) {
  return (
    <Box position="relative" gridColumn={gridColumn}>
      <Text
        position="absolute"
        top="-9px"
        left="12px"
        px="4px"
        bg="white"
        fontSize="xs"
        color="gray.500"
        zIndex={1}
      >
        {label}
        {required && (
          <Text as="span" color="red.500">
            {" "}
            *
          </Text>
        )}
      </Text>
      {children}
      {error && (
        <Text color="red.500" fontSize="xs" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  );
}

function UserRegistration() {
  const toast = useToast();
  const primaryTextColor = "gray.800";
  const focusColor = "#aa2525";

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadRoles() {
      try {
        const response = await getRoles();
        if (isMounted) {
          setRoles(response.data || []);
        }
      } catch (error) {
        console.error("[USER REGISTRATION] Failed to load roles", error);
        toast({
          title: "Error",
          description: "Failed to load roles. Please refresh the page.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } finally {
        if (isMounted) setRolesLoading(false);
      }
    }

    loadRoles();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedRole = roles.find(
    (r) => String(r.roleId) === String(formData.roleId)
  );
  const showAuditorCode = isSalesRole(selectedRole?.roleName);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.userName) newErrors.userName = "Username is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Enter a valid email address";
    }
    if (!formData.password) newErrors.password = "Password is required";
    if (!formData.roleId) newErrors.roleId = "Role is required";
    if (showAuditorCode && !formData.auditorCode) {
      newErrors.auditorCode = "Auditor code is required";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        userName: formData.userName,
        email: formData.email,
        password: formData.password,
        role: selectedRole?.roleName,
        roleId: Number(formData.roleId),
      };

      if (showAuditorCode) {
        payload.auditorCode = Number(formData.auditorCode);
      }

      await register(payload);

      toast({
        title: "User registered",
        description: `${formData.userName} has been created successfully.`,
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top",
      });

      setFormData(EMPTY_FORM);
      setErrors({});
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to register user. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = {
    h: "48px",
    borderColor: "gray.400",
    borderRadius: "6px",
    px: 4,
    fontSize: "sm",
    _hover: { borderColor: "gray.400" },
    _focus: {
      borderColor: focusColor,
      boxShadow: `0 0 0 1px ${focusColor}`,
    },
  };

  return (
    <Flex
      className="user-registration-page"
      minH="100vh"
      align="flex-start"
      justify="center"
      px={{ base: 6, md: 10 }}
      pb={{ base: 6, md: 10 }}
      pt={{ base: "120px", md: "95px" }}
    >
      <Box w="100%" maxW="1000px">
        <Text
          fontSize="2xl"
          fontWeight="bold"
          color={primaryTextColor}
          mb={10}
          textAlign="center"
        >
          User Registration
        </Text>

        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="12px"
          boxShadow="sm"
          p={{ base: 6, md: 10 }}
        >
          <form onSubmit={handleSubmit}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacingX={10} spacingY={12}>
              <FieldFrame label="Username" required error={errors.userName}>
                <Input
                  name="userName"
                  type="text"
                  placeholder="Username"
                  value={formData.userName}
                  onChange={handleChange}
                  {...fieldStyle}
                />
              </FieldFrame>

              <FieldFrame label="Email" required error={errors.email}>
                <Input
                  name="email"
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  {...fieldStyle}
                />
              </FieldFrame>

              <FieldFrame label="Password" required error={errors.password}>
                <Input
                  name="password"
                  type="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  {...fieldStyle}
                />
              </FieldFrame>

              <FieldFrame label="Role" required error={errors.roleId}>
                <Select
                  name="roleId"
                  placeholder={
                    rolesLoading ? "Loading roles..." : "Select a role"
                  }
                  value={formData.roleId}
                  onChange={handleChange}
                  isDisabled={rolesLoading}
                  {...fieldStyle}
                >
                  {roles.map((role) => (
                    <option key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </option>
                  ))}
                </Select>
              </FieldFrame>

              {showAuditorCode && (
                <FieldFrame label="Auditor Code" required error={errors.auditorCode}>
                  <Input
                    name="auditorCode"
                    type="number"
                    placeholder="Auditor code"
                    value={formData.auditorCode}
                    onChange={handleChange}
                    {...fieldStyle}
                  />
                </FieldFrame>
              )}
            </SimpleGrid>

            <Flex justify="flex-end" mt={10}>
              <Button
                type="submit"
                colorScheme="red"
                borderRadius="6px"
                px={10}
                isLoading={submitting}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </Box>
      </Box>
    </Flex>
  );
}

export default UserRegistration;